export const blockPatterns: RegExp[] = [
  // 1. Phone Numbers - Only with explicit sharing context
  /(?:call|contact|reach|text|message|dm)\s+(?:me|us)\s+(?:on|at|via)?\s*[:@]?\s*(?:\+91[\s\-\.]?)?[6-9]\d{9}/gi,
  
  // 2. Phone with +91 prefix only (clear international intent)
  /\+91[\s\-\.]?[6-9]\d{9}/g,
  
  // 3. WhatsApp links only (not the word)
  /wa\.me\/\d{10,}/gi,
  
  // 4. Email with explicit context only
  /(?:email|mail|contact)\s+(?:me|us)\s+(?:at|on)?\s*[:@]?\s*[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi,
  
  // 5. Social media handles with @ symbol
  /(?:instagram|insta|telegram|tg)\s*[@:]\s*@?[a-z0-9_.]{3,}/gi,
  
  // 6. UPI handles only
  /\b[a-z0-9.-]{3,}@(?:upi|paytm|phonepe|gpay|googlepay)\b/gi,
];

// Minimal suspicious patterns
export const suspiciousPatterns: RegExp[] = [
  /(?:contact|message)\s+(?:me\s+)?outside\s+(?:this\s+)?platform/gi,
  /share\s+(?:your|my)\s+whatsapp/gi,
];


// Enhanced blocking function for general messages
export const isBlockedMessage = (message: string): { 
  isBlocked: boolean; 
  reason: string; 
  severity: 'warning' | 'violation' 
} => {
  const originalMessage = message;
  
  // Check primary blocking patterns
  for (const pattern of blockPatterns) {
    if (pattern.test(originalMessage)) {
      return {
        isBlocked: true,
        reason: 'Contact information sharing detected',
        severity: 'violation'
      };
    }
  }
  
  // Check suspicious patterns
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(originalMessage)) {
      return {
        isBlocked: true,
        reason: 'Suspicious content detected',
        severity: 'warning'
      };
    }
  }
  
  return {
    isBlocked: false,
    reason: '',
    severity: 'warning'
  };
};

// VERY LIBERAL: delivery address validation
export const isDeliveryDetailsBlocked = (text: string): { 
  isBlocked: boolean; 
  reason: string; 
  severity: 'warning' | 'violation' 
} => {
  if (!text || text.trim().length === 0) {
    return {
      isBlocked: false,
      reason: '',
      severity: 'warning'
    };
  }

  const originalText = text.trim();
  
  // 1. Only block 10-digit phone with +91 OR standalone 10-digit number
  // BUT allow if it contains address keywords
  const hasAddressKeywords = /(?:house|flat|plot|block|sector|phase|floor|room|unit|door|building|street|road|lane|colony|area|near)/gi.test(originalText);
  
  if (!hasAddressKeywords) {
    // Only check for phone if no address context
    const phonePattern = /(?:\+91[\s\-\.]?[6-9]\d{9})|(?:\b[6-9]\d{9}\b)/g;
    if (phonePattern.test(originalText)) {
      return {
        isBlocked: true,
        reason: 'Phone numbers should not be included in address',
        severity: 'violation'
      };
    }
  }
  
  // 2. Email with @ symbol
  if (/@[a-z0-9.-]+\.[a-z]{2,}/gi.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Email addresses should not be included in address',
      severity: 'violation'
    };
  }
  
  // 3. WhatsApp links only
  if (/wa\.me\//gi.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'WhatsApp links are not allowed',
      severity: 'violation'
    };
  }
  
  // 4. Social media handles with @
  if (/@[a-z0-9_]{3,}/gi.test(originalText) && /(instagram|telegram)/gi.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Social media handles should not be included in address',
      severity: 'violation'
    };
  }
  
  // 5. UPI handles
  if (/@(?:upi|paytm|phonepe|gpay)/gi.test(originalText)) {
    return {
      isBlocked: true,
      reason: 'Payment handles should not be included in address',
      severity: 'violation'
    };
  }
  
  return {
    isBlocked: false,
    reason: '',
    severity: 'warning'
  };
};

// User violation tracking (unchanged)
interface UserViolation {
  userId: string;
  violations: number;
  warnings: number;
  lastViolation: Date;
  isFlagged: boolean;
  isBanned: boolean;
}

class ViolationTracker {
  private violations: Map<string, UserViolation> = new Map();
  private readonly MAX_WARNINGS = 3;
  private readonly MAX_VIOLATIONS = 3;
  
  recordViolation(userId: string, severity: 'warning' | 'violation'): UserViolation {
    const existing = this.violations.get(userId) || {
      userId,
      violations: 0,
      warnings: 0,
      lastViolation: new Date(),
      isFlagged: false,
      isBanned: false
    };
    
    if (severity === 'violation') {
      existing.violations++;
    } else {
      existing.warnings++;
    }
    
    existing.lastViolation = new Date();
    existing.isFlagged = existing.warnings >= this.MAX_WARNINGS || existing.violations >= 2;
    existing.isBanned = existing.violations >= this.MAX_VIOLATIONS;
    
    this.violations.set(userId, existing);
    return existing;
  }
  
  getUserStatus(userId: string): UserViolation | null {
    return this.violations.get(userId) || null;
  }
  
  isUserBanned(userId: string): boolean {
    const user = this.violations.get(userId);
    return user?.isBanned || false;
  }
  
  isUserFlagged(userId: string): boolean {
    const user = this.violations.get(userId);
    return user?.isFlagged || false;
  }
}

export const violationTracker = new ViolationTracker();